import { API_BASE } from "@/lib/utils";

function appendListFilters(params, { q, segment, ticket, sortBy, sortDir }) {
  if (q?.trim()) params.set("q", q.trim());
  if (segment && segment !== "all") params.set("segment", segment);
  if (ticket && ticket !== "all") params.set("ticket", ticket);
  const coreSort = ["name", "email", "company", "ticket", "seat", "status", "country", "mobile", "created_at", "updated_at"];
  if (sortBy && coreSort.includes(sortBy)) params.set("sort", sortBy);
  else if (sortBy) {
    params.set("sort_custom_field", sortBy);
    params.set("sort", "name");
  }
  if (sortDir) params.set("sort_dir", sortDir);
}

/** Fetch every attendee id+status matching current list filters (for select-all). */
export async function fetchAllAttendeesInView(eventId, filters = {}) {
  const pageSize = 5000;
  const all = [];
  let offset = 0;
  let total = 0;
  do {
    const params = new URLSearchParams({
      minimal: "1",
      limit: String(pageSize),
      offset: String(offset),
    });
    appendListFilters(params, filters);
    const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees?${params}`);
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    const batch = Array.isArray(data.attendees) ? data.attendees : [];
    total = Number(data.total) || batch.length;
    all.push(...batch);
    offset += pageSize;
    if (batch.length === 0) break;
  } while (all.length < total);
  return all;
}

export async function fetchTicketTypes(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/ticket-types`);
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return Array.isArray(data.tickets) ? data.tickets : [];
}

export async function fetchImportSchema(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/import-schema`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function importMappedCsv(eventId, body) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/import-mapped`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Import failed");
  return data;
}

export async function bulkUpdateAttendees(eventId, updates) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/bulk-update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Bulk update failed");
  return data;
}

export function exportCsvUrl(eventId, { ids, columns }) {
  const params = new URLSearchParams();
  if (ids?.length) params.set("ids", ids.join(","));
  if (columns?.length) params.set("columns", columns.join(","));
  const q = params.toString();
  return `${API_BASE}/api/events/${eventId}/attendees/export.csv${q ? `?${q}` : ""}`;
}

export async function fetchDuplicateGroups(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/duplicate-groups`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function scanDuplicateGroups(eventId, criteria) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/duplicate-scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ criteria }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Duplicate scan failed");
  return data;
}

export async function resolveDuplicateGroup(eventId, body) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/duplicate-resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Could not resolve duplicate group");
  return data;
}

export async function deleteAttendee(eventId, attendeeId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/${attendeeId}`, {
    method: "DELETE",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Delete failed");
  return data;
}

export async function bulkDeleteAttendees(eventId, ids) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/bulk-delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Bulk delete failed");
  return data;
}

export async function deduplicateGroup(eventId, groupId, keepIds) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees/deduplicate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ groupId, keepIds }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Deduplicate failed");
  return data;
}

export function parseCsvHeaders(text) {
  const line = (text || "").split(/\r?\n/).find((l) => l.trim());
  if (!line) return [];
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQ = !inQ;
      continue;
    }
    if (ch === "," && !inQ) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out.filter(Boolean);
}
